import { Inject, Injectable } from '@angular/core';

import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { Store } from '@ngrx/store';

import {
    ILinearBetslipMessagesProviderService,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from '../../../../base/services/betslip-messages-provider.service';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipTrackingData } from '../../../tracking/tracking.models';
import BetslipErrorsActions from '../../actions';
import { ErrorOrigin } from '../../errors/betslip-error';
import { ErrorViewModel } from '../../models/models';
import { IExtendedMessage, isExtendedMessage } from '../formatting/message-formatters/message-formatter';

@Injectable()
export class ClientErrorTrackingService {
    private readonly offerChanged: string;
    private readonly nonCombinableSystem: string;
    private readonly nonCombinableMulti: string;

    constructor(
        private store: Store,
        @Inject(LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: ILinearBetslipMessagesProviderService,
    ) {
        const combinedMessages = messageProvider.getMessages();

        const messages = combinedMessages.betslip;

        this.offerChanged = messages.common.GlobalToastMessage;
        this.nonCombinableSystem = combinedMessages.linear.NonCombinableSystem;
        this.nonCombinableMulti = combinedMessages.linear.NonCombinableMulti;
    }

    trackAllErrors(errors: ErrorViewModel[], isComboPrevention: boolean, betslipType: BetslipType | null, isOfferChanged: boolean): void {
        const trackingBetslipErrorsData = this.getFullErrorData(errors, isComboPrevention, betslipType, isOfferChanged);

        if (!trackingBetslipErrorsData) {
            return;
        }

        this.store.dispatch(BetslipErrorsActions.trackBetslipClientErrors({ trackingData: trackingBetslipErrorsData }));
    }

    /**
     * Gets the first trackable error based on all current errors. Currently, only the first trackable error will be tracked per collection of errors.
     * @param errors The collection of errors to check.
     * @returns The error that will be tracked or undefined.
     */
    getFirstTrackableError(errors: ErrorViewModel[]): ErrorViewModel | undefined {
        return errors.find((error) => error.error.origin !== ErrorOrigin.BetPlacement);
    }

    private getFullErrorData(
        errors: ErrorViewModel[],
        hasComboPrevention: boolean,
        betslipType: BetslipType | null,
        isOfferChanged: boolean,
    ): BetslipTrackingData | null {
        if (hasComboPrevention) {
            return {
                eventDetails: betslipType === BetslipType.System ? this.nonCombinableSystem : this.nonCombinableMulti,
                positionEvent: this.getPositioneventForTracking(PlacementErrorType.ComboPrevention),
            };
        }

        if (isOfferChanged) {
            return { eventDetails: this.offerChanged, positionEvent: this.getPositioneventForTracking('offerchanged') };
        }

        return this.getCommonErrorData(errors);
    }

    private getCommonErrorData(errors: ErrorViewModel[]): BetslipTrackingData | null {
        const firstError = this.getFirstTrackableError(errors);

        if (!firstError) {
            return null;
        }

        return {
            eventDetails: this.hasTitle(firstError.message) ? firstError.message.message : (firstError.message ?? ''),
            positionEvent: this.getPositioneventForTracking(firstError.error.type),
        };
    }

    private getPositioneventForTracking(errorType: string): string {
        return 'error message - ' + errorType;
    }

    private hasTitle(error: string | IExtendedMessage | null): error is IExtendedMessage {
        return isExtendedMessage(error) && !!error.title;
    }

    //in case of quickbet we can't have comboprevention - we closing it - and do not care about betslipType
    trackQuickBetErrors(errors: ErrorViewModel[], isOfferChanged: boolean, hasComboPrevention: boolean = false, betslipType = null): void {
        const trackingBetslipErrorsData = this.getFullErrorData(errors, hasComboPrevention, betslipType, isOfferChanged);
        if (!trackingBetslipErrorsData) {
            return;
        }
        this.store.dispatch(BetslipErrorsActions.trackQuickBetClientErrors({ trackingData: trackingBetslipErrorsData }));
    }
}
